import { useAuth } from '../contexts/AuthContext';

const API_URL = import.meta.env.VITE_API_URL || '';

interface ApiOptions {
    method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
    body?: unknown;
    params?: Record<string, string | number | boolean | undefined>;
}

export class ApiError extends Error {
    status: number;
    code?: string;
    // Détail brut d'une erreur amont (Dynamics, Pappers, SharePoint) — le
    // backend ne le renvoie qu'aux admins (cf. error.middleware.ts), absent
    // pour les autres utilisateurs.
    details?: string;
    constructor(message: string, status: number, code?: string, details?: string) {
        super(message);
        this.status = status;
        this.code = code;
        this.details = details;
    }
}

async function fetchApi<T>(endpoint: string, options: ApiOptions = {}, token: string | null): Promise<T> {
    const { method = 'GET', body, params } = options;

    let url = `${API_URL}${endpoint}`;

    if (params) {
        const searchParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined) {
                searchParams.append(key, String(value));
            }
        });
        const queryString = searchParams.toString();
        if (queryString) {
            url += `?${queryString}`;
        }
    }

    const headers: HeadersInit = { 'Content-Type': 'application/json' };
    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(url, {
        method,
        headers,
        body: body ? JSON.stringify(body) : undefined,
    });

    const contentType = response.headers.get('content-type') || '';
    if (!contentType.includes('application/json')) {
        throw new ApiError('Réponse inattendue du serveur', response.status);
    }

    const data = await response.json();

    if (!response.ok) {
        throw new ApiError(data.error?.message || 'API request failed', response.status, data.error?.code, data.error?.details);
    }

    return data;
}

/**
 * Envoi de fichiers. On ne fixe pas `Content-Type` : le navigateur doit
 * composer lui-même l'en-tête multipart avec sa frontière, et l'écraser casse
 * l'analyse côté serveur.
 */
async function uploadApi<T>(
    endpoint: string,
    form: FormData,
    token: string | null,
    method: 'POST' | 'PUT' = 'POST',
): Promise<T> {
    const headers: HeadersInit = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const response = await fetch(`${API_URL}${endpoint}`, { method, headers, body: form });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
        throw new ApiError(data.error?.message || 'Envoi refusé', response.status, data.error?.code);
    }
    return data as T;
}

/**
 * Récupère un binaire et déclenche son téléchargement.
 *
 * Passer par `fetch` plutôt que par un lien direct est nécessaire : le jeton
 * applicatif voyage dans un en-tête, et une navigation de navigateur ne le
 * porterait pas.
 */
async function downloadApi(
    endpoint: string,
    token: string | null,
    filename: string,
    method: 'GET' | 'POST' = 'GET',
): Promise<void> {
    const headers: HeadersInit = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const response = await fetch(`${API_URL}${endpoint}`, { method, headers });
    if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new ApiError(
            data.error?.message || 'Téléchargement impossible',
            response.status,
            data.error?.code,
        );
    }

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
}

export function useApi() {
    const { token } = useAuth();

    return {
        get: <T>(endpoint: string, params?: Record<string, any>) =>
            fetchApi<T>(endpoint, { method: 'GET', params }, token),
        post: <T>(endpoint: string, body?: any) =>
            fetchApi<T>(endpoint, { method: 'POST', body }, token),
        put: <T>(endpoint: string, body?: any) =>
            fetchApi<T>(endpoint, { method: 'PUT', body }, token),
        // Modification partielle : n'envoie que les champs à changer, les autres
        // restant en place côté serveur.
        patch: <T>(endpoint: string, body?: any) =>
            fetchApi<T>(endpoint, { method: 'PATCH', body }, token),
        // Un corps est admis : supprimer plusieurs éléments désignés est une
        // suppression, pas une modification, et le dire dans l'URL obligerait à
        // y encoder une liste.
        delete: <T>(endpoint: string, body?: any) =>
            fetchApi<T>(endpoint, { method: 'DELETE', body }, token),
        upload: <T>(endpoint: string, form: FormData, method?: 'POST' | 'PUT') =>
            uploadApi<T>(endpoint, form, token, method),
        download: (endpoint: string, filename: string, method?: 'GET' | 'POST') =>
            downloadApi(endpoint, token, filename, method),
    };
}
